import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./CommunityBlockedModal.scss";
import { getAccessToken } from "../../awsAuth";

const DEFAULT_AVATAR =
  "https://cvanto-photos.s3.amazonaws.com/DefaultAvatar/avatar.png";

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (typeof window !== "undefined" &&
  window.location.hostname.includes("onrender.com")
    ? "https://cvanto-api.onrender.com"
    : "http://localhost:5000");

const CommunityBlockedModal = ({ communityId, adminId, onClose }) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [blocked, setBlocked] = useState([]);
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => onClose?.(), 200);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!communityId) return;
      setLoading(true);
      setErr("");

      try {
        const token = await getAccessToken();

        if (!token) {
          throw new Error("NO_TOKEN");
        }

        const res = await fetch(
          `${API_BASE}/api/block/community/${communityId}/blocked/profiles`,
          {
            cache: "no-store",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          console.error("community blocked profiles error:", data);
          throw new Error(data.error || "HTTP " + res.status);
        }

        if (cancelled) return;
        setBlocked(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("❌ blocked list:", e);
        if (!cancelled) {
          setErr("Не вдалося отримати список заблокованих");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [communityId]);

  const onUnblock = async (userId) => {
    if (!communityId || !userId) return;

    try {
      const token = await getAccessToken();

      if (!token) {
        alert("Потрібна авторизація");
        return;
      }

      const res = await fetch(
        `${API_BASE}/api/block/community/${communityId}/block/${userId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({}),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        console.error("community unblock error:", data);
        throw new Error(data.error || "HTTP " + res.status);
      }

      setBlocked((prev) => prev.filter((u) => u.id !== userId));
    } catch (e) {
      console.error("❌ unblock:", e);
      alert("Не вдалося розблокувати користувача.");
    }
  };

  const hasItems = useMemo(() => blocked.length > 0, [blocked]);

  const goProfile = useCallback(
    (uid) => {
      onClose?.();
      navigate(`/profile/${uid}`);
    },
    [navigate, onClose]
  );

  const onUserKeyDown = (e, uid) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      goProfile(uid);
    }
  };

  return (
    <div
      className={`cb-backdrop ${isOpen ? "is-open" : ""}`}
      onClick={handleClose}
    >
      <div
        className={`cb-modal ${isOpen ? "is-open" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="cb-header">
          <h3>Заблоковані учасники</h3>
          <button className="cb-close" onClick={handleClose} aria-label="Закрити">
            ✕
          </button>
        </header>

        <div className="cb-content">
          {loading && <p className="hint">Завантаження…</p>}
          {!loading && err && <p className="error">{err}</p>}
          {!loading && !err && !hasItems && (
            <p className="hint">Немає заблокованих.</p>
          )}

          {!loading && !err && hasItems && (
            <ul className="cb-list">
              {blocked.map((u, i) => (
                <li
                  key={u.id}
                  className="cb-row"
                  style={{ animationDelay: `${i * 0.04}s` }}
                >
                  <div
                    className="cb-user"
                    role="button"
                    tabIndex={0}
                    onClick={() => goProfile(u.id)}
                    onKeyDown={(e) => onUserKeyDown(e, u.id)}
                    title={`Перейти до профілю ${u.username || ""}`}
                  >
                    <img
                      src={u.avatar || DEFAULT_AVATAR}
                      alt={u.username || "Користувач"}
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = DEFAULT_AVATAR;
                      }}
                    />
                    <span>{u.username || "Користувач"}</span>
                  </div>

                  <button
                    className="cb-unblock"
                    onClick={() => onUnblock(u.id)}
                    title="Розблокувати"
                  >
                    Розблокувати
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommunityBlockedModal;